import cn from 'classnames';
import { Icon, P, Stack } from '@fashionnovateam/nova-ui-toolkit';
import { ReactNode, useEffect, useMemo, useState } from 'react';

export type DateFilterValue = {
  from: string;
  to: string;
};

type DateFilterCardProps = {
  title?: string;
  value: DateFilterValue | undefined;
  getCount?: (isActive: boolean, value?: DateFilterValue) => Promise<string>;
  isActive?: boolean;
  fixedWidth?: boolean;
  customSubtitle?: ReactNode;
};

const formatDate = (date: Date | string) => {
  const v = new Date(date);
  const monthAbbreviations = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  return `${monthAbbreviations[v.getUTCMonth()]} ${v.getUTCDate()}`;
};

export const getTitleCard = (value: DateFilterValue) =>
  `${formatDate(value.from)} - ${formatDate(value.to)}`;

export function DateFilterCard({
  title,
  value,
  getCount,
  isActive = false,
  fixedWidth = false,
  customSubtitle,
}: DateFilterCardProps) {
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState('');

  const computedTitle = useMemo(() => {
    if (title) return title;
    if (!value) return 'All';
    return getTitleCard(value);
  }, [title, value]);

  useEffect(() => {
    if (getCount) {
      setLoading(true);
      getCount(isActive, value)
        .then((v) => {
          setCount(v);
        })
        .catch((error) => {
          console.error('Failed to get count', error);
          setCount('');
        })
        .finally(() => setLoading(false));
    } else {
      setCount(computedTitle === 'Custom' ? 'Select Range' : '');
      setLoading(false);
    }
  }, [getCount, value?.from, value?.to, isActive]);

  return (
    <Stack
      direction="col"
      align="center"
      className={cn(
        ` ${fixedWidth && 'w-[114px]'}`,
        `py-[14px] ${isActive ? 'bg-neutral-200' : 'bg-neutral-0'}`,
      )}
    >
      <P size="xsm" color="secondary" weight="semibold">
        {computedTitle}
      </P>
      {customSubtitle ? (
        <P size="2xs" color="disabled">
          {loading ? (
            <Icon.LoadingCircle className="mt-[5px] inline" />
          ) : (
            `${count} `
          )}{' '}
          {customSubtitle}
        </P>
      ) : (
        <P size="2xs" color="disabled">
          {loading ? <Icon.LoadingCircle className="mt-[5px]" /> : count}
        </P>
      )}
    </Stack>
  );
}
